/* eslint-disable no-nested-ternary */
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import ContTitle from '@wconcept/wconcept-ui-renewal/components/molecules/_title/ContTitle';
import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import ItemsScrollBar from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsScrollBar';
import ItemsGrid from '@wconcept/wconcept-ui-renewal/components/atoms/ItemsGrid';
import BasicBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/BasicBtn';
import CountdownTimer from '@wconcept/wconcept-ui-renewal/components/molecules/CountdownTimer';
import CategoryMenuBar from '@wconcept/wconcept-ui-renewal/components/organisms/_menuBar/CategoryMenuBar';
import CouponItem2 from '@wconcept/wconcept-ui-renewal/components/molecules/_item/CouponItem2';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import * as S from './style';

interface TabState {
  hasMore: boolean;
  isExpanded: boolean;
}

interface CustomColorType {
  bgColor?: string;
  textColor?: string;
  pointColor?: string;
  emphasisTextColor?: string;
}

type ColorType = 'white' | 'black' | CustomColorType | string;

interface IProps {
  imgSrc?: string;
  title?: string;
  subTitle?: string;
  status?: '1' | '2' | '3' | undefined; // 1: 마감 | 2: 진행중 | 3: 진행 예정
  colorType?: ColorType;
  startDate: string | undefined;
  endDate: string | undefined;
  tabMenuList?: React.JSX.Element | React.JSX.Element[];
  selDateTab?: string;
  categoryTabType?: 'type1' | 'type7';
  categoryTabList?: React.ReactNode[];
  productList?: React.ReactNode;
  isDesktop?: boolean;
  serverTime?: string | Date | undefined;
  onLoadMore?: (tabId: string) => void;
  selectedTabId?: string;
  selectedCateTabId?: string;
  tabStates?: { [key: string]: TabState } | undefined;
  onClickMore?: () => void;
  isMore?: boolean;
  isLink?: boolean;
  couponTitle?: string;
  couponDesc?: string;
  onClickCoupon?: () => void;
  countdownDesc?: string;
}

const SpecialPriceProductSection = ({
  imgSrc,
  title,
  subTitle,
  status,
  colorType = 'white',
  startDate = '',
  endDate = '',
  tabMenuList,
  selDateTab,
  categoryTabType = 'type7',
  categoryTabList,
  productList,
  isDesktop,
  serverTime,
  onLoadMore,
  selectedTabId,
  selectedCateTabId,
  tabStates,
  onClickMore,
  isMore,
  isLink = false,
  couponTitle,
  couponDesc,
  onClickCoupon,
  countdownDesc,
}: IProps) => {
  const isCustomColor = typeof colorType === 'object';
  const customColors = isCustomColor ? colorType : undefined;

  const cName = classNameBind([
    'special-price-product',
    !isCustomColor ? `bg-type-${colorType}` : '',
    imgSrc ? 'has-img-banner' : '',
  ]);

  const [targetDate, setTargetDate] = useState<string>('');
  const [eventStatus, setEventStatus] = useState<'1' | '2' | '3'>(
    status || '2'
  );
  const [startDay, setStartDay] = useState('');
  const [startHour, setStartHour] = useState('');
  const [amPm, setAmPm] = useState('오전');
  const [isExpired, setIsExpired] = useState(false);
  const [serverTimeOffset, setServerTimeOffset] = useState<number | null>(null);
  const [currentTabState, setCurrentTabState] = useState<TabState | undefined>(
    undefined
  );

  useEffect(() => {
    if (selectedTabId && tabStates && selectedTabId in tabStates) {
      setCurrentTabState(tabStates[selectedTabId]);
    } else {
      // setCurrentTabState(undefined);
      setCurrentTabState({ hasMore: true, isExpanded: false });
    }
  }, [selectedTabId, tabStates]);

  useEffect(() => {
    const fetchServerTime = () => {
      if (serverTime) {
        const serverTimeMs = new Date(serverTime).getTime();
        const localTimeMs = new Date().getTime();
        setServerTimeOffset(serverTimeMs - localTimeMs);
      } else {
        setServerTimeOffset(0);
      }
    };
    fetchServerTime();
  }, [serverTime]);

  const getAdjustedTime = useCallback(
    () => new Date(Date.now() + (serverTimeOffset ?? 0)),
    [serverTimeOffset]
  );

  // 시작일/종료일에 따라 상태 업데이트
  const updateDatesAndStatus = useCallback(() => {
    if (serverTimeOffset === null || !startDate || !endDate) return;

    const now = getAdjustedTime();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      setTargetDate(startDate);
      setEventStatus('3');
    } else if (now >= start && now < end) {
      setTargetDate(endDate);
      setEventStatus('2');
    } else {
      setTargetDate('');
      setEventStatus('1');
    }

    if (startDate) {
      const parsedDate = new Date(startDate);
      const hours = parsedDate.getHours();
      setStartDay(parsedDate.getDate().toString().padStart(2, '0'));
      setStartHour(hours % 12 === 0 ? '12' : (hours % 12).toString());

      // 오전/오후 설정
      setAmPm(parsedDate.getHours() < 12 ? '오전' : '오후');
    }
  }, [startDate, endDate, getAdjustedTime, serverTimeOffset]);

  // 시작일/종료일이 변경될 때마다 상태 업데이트
  useEffect(() => {
    updateDatesAndStatus();
  }, [startDate, endDate, updateDatesAndStatus]);

  useEffect(() => {
    // 카운트다운 종료 감지를 위한 isExpired 상태 확인
    if (serverTimeOffset === null) return;

    const checkExpiration = () => {
      if (!targetDate) {
        setIsExpired(true);
        return;
      }

      const now = getAdjustedTime();
      const target = new Date(targetDate);
      setIsExpired(now >= target);

      // 만료 시 이벤트 상태 변경
      if (now >= target && eventStatus === '2') {
        setEventStatus('1');
      }
    };

    checkExpiration();
    const interval = setInterval(checkExpiration, 1000);
    // eslint-disable-next-line consistent-return
    return () => clearInterval(interval);
  }, [targetDate, getAdjustedTime, serverTimeOffset, eventStatus]);

  const handleLoadMore = useCallback(() => {
    if (onLoadMore && selectedTabId) {
      onLoadMore(selectedTabId);
    } else {
      setCurrentTabState({ hasMore: true, isExpanded: true });
    }
  }, [onLoadMore, selectedTabId]);

  const showMoreButton = useMemo(
    () => currentTabState?.hasMore && !currentTabState?.isExpanded,
    [currentTabState]
  );

  const dateTabSelectedIndex = useMemo(() => {
    if (!Array.isArray(tabMenuList) || selectedTabId === undefined)
      return undefined;
    const idx = tabMenuList.findIndex(
      (item: { props?: { value?: string | number } } | null) =>
        item?.props?.value != null &&
        String(item.props.value) === String(selectedTabId)
    );
    return idx === -1 ? undefined : idx;
  }, [tabMenuList, selectedTabId]);

  return (
    <S.SpecialPriceProductSection
      className={cName}
      $bgColor={customColors?.bgColor}
      $textColor={customColors?.textColor}
      $pointColor={customColors?.pointColor}
      $emphasisTextColor={customColors?.emphasisTextColor}
    >
      {imgSrc && <Img src={imgSrc} alt={title} className="img-banner" />}

      <ContTitle
        title={title}
        subTitle={subTitle}
        titleColor={customColors?.textColor}
        subTitleColor={customColors?.textColor}
      />

      {tabMenuList && Array.isArray(tabMenuList) && tabMenuList.length > 0 && (
        <>
          {isDesktop ? (
            tabMenuList.length > 15 ? (
              <CategoryMenuBar
                fold
                items={Array.isArray(tabMenuList) ? tabMenuList : [tabMenuList]}
                isDesktop={isDesktop}
                selectedValue={selDateTab}
                className="date-tab"
              />
            ) : (
              <div className="date-tab">{tabMenuList}</div>
            )
          ) : (
            <ItemsScrollBar
              perView={0}
              colGap={8}
              className="date-tab"
              selectedIndex={dateTabSelectedIndex}
            >
              {tabMenuList}
            </ItemsScrollBar>
          )}

          <CountdownTimer
            desc={countdownDesc ?? `매일 ${amPm} ${startHour}시 한정 특가`}
            targetDate={targetDate}
            serverTimeOffset={serverTimeOffset}
            onExpired={expired => setIsExpired(expired)}
            eventStatus={eventStatus}
            bgType={
              colorType === 'white'
                ? 'gray'
                : colorType === 'black'
                ? 'black'
                : 'opacity'
            }
          />
        </>
      )}

      {couponTitle && (
        <div className="coupon-wrap">
          <CouponItem2
            title={couponTitle}
            desc={couponDesc}
            status={eventStatus === '3' ? 'comingsoon' : 'download'}
            colorType={
              isCustomColor
                ? customColors
                : colorType === 'white'
                ? 'black'
                : 'gray'
            }
            onClick={onClickCoupon}
          />
        </div>
      )}

      {Array.isArray(categoryTabList) &&
        categoryTabList.length > 0 &&
        (categoryTabType === 'type1' && isDesktop ? (
          <div className="category-tab">{categoryTabList}</div>
        ) : (
          <CategoryMenuBar
            type={categoryTabType}
            bgType={isCustomColor || colorType !== 'white' ? 'black' : 'white'}
            items={categoryTabList}
            selectedValue={selectedCateTabId}
            className="category-tab"
          />
        ))}
      <S.ProductList
        className={`product-wrap ${
          currentTabState?.isExpanded ? 'more-product' : ''
        } event-status-${eventStatus} ${showMoreButton ? 'more-button' : ''}`}
      >
        <div className="product-list">
          <ItemsGrid
            perView={3}
            rowGap={24}
            colGap={5.5}
            md={{ perView: 3 }}
            xl={{ perView: 6, rowGap: 40, colGap: 18.8 }}
          >
            {productList}
          </ItemsGrid>
        </div>
        {!isMore && (
          <BasicBtn
            className="btn-section-more"
            text={isLink ? '상품 전체 보기' : '상품 더보기'}
            type={isLink ? 'line' : 'lineDropdown'}
            onClick={onClickMore}
          />
        )}
        {eventStatus === '3' && <div className="coming-soon" />}
      </S.ProductList>
    </S.SpecialPriceProductSection>
  );
};

export default memo(SpecialPriceProductSection);
